/**
 * WhatsApp GROUP @mentions — turning what the agent composed into what
 * WhatsApp needs on the wire.
 *
 * WhatsApp binds a mention to a participant only when BOTH the visible
 * `@<digits>` token (the participant's jid digits — `@lid` in LID groups) and
 * the matching jid in the mention metadata are present. The composer's picker
 * inserts a mention chip that serializes to markdown
 * `[@Name](mention://user/<digits>/<name>)`; at send time the chip is rewritten
 * to the bare token and the jids are collected from the tokens.
 *
 * A chip can also be LOST before send — the draft re-seed used to flatten it to
 * plain "@Name" (fixed 2026-08-12), and agents type "@Wan Smith3D" by hand
 * without picking from the dropdown. In reply mode a chip and typed text look
 * the same, so staff only find out on the phone that nobody was pinged (S3
 * bug-report group, 2026-08-26). So a plain `@<alias>` that exactly matches ONE
 * roster member is ALSO rewritten to the token. Aliases: the display name (and
 * the chip label form — names lose their ()[] when they become a chip), the
 * phone digits, and the WhatsApp @username. An alias shared by two members is
 * never rewritten (the text stays as typed) — guessing would ping the wrong
 * person with no visible cue.
 */

const MENTION_LINK_REGEX = /\[@[^\]]*\]\(mention:\/\/user\/(\d+)\/[^)]+\)/g;

const escapeRegExp = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const fold = value => value.trim().toLowerCase();

// One { alias, digits } row per distinct alias of each member, ambiguous
// aliases dropped, longest first so "Wan Smith3D" wins over a member "Wan".
const aliasRows = participants => {
  const owners = new Map(); // folded alias -> Set(digits)
  const rows = [];
  (participants || []).forEach(p => {
    if (!p || !p.digits) return;
    const digits = String(p.digits);
    const name = String(p.name || '').trim();
    const aliases = [
      name,
      name.replace(/[()[\]]/g, '').trim(),
      String(p.phone || '').replace(/\D/g, ''),
      String(p.username || '').trim(),
    ].filter(alias => alias && alias !== digits);
    new Set(aliases.map(fold)).forEach(alias => {
      if (!owners.has(alias)) owners.set(alias, new Set());
      owners.get(alias).add(digits);
      rows.push({ alias, digits });
    });
  });
  return rows
    .filter(({ alias }) => owners.get(alias).size === 1)
    .sort((a, b) => b.alias.length - a.alias.length);
};

/**
 * Rewrite mention chips and exact `@<alias>` text to `@<digits>` tokens.
 * @param {string} text - outgoing markdown/text
 * @param {Array} participants - group roster rows ({ digits, name, phone, username })
 * @returns {string}
 */
export const convertGroupMentionsToTokens = (text, participants = []) => {
  let output = (text || '').replace(MENTION_LINK_REGEX, '@$1');

  aliasRows(participants).forEach(({ alias, digits }) => {
    // "@alias" only counts at the start of the text, after whitespace or after
    // an opening bracket, and must end at a word boundary — so a URL handle
    // ("tiktok.com/@wan"), an email ("x@CY.com") or a longer word ("@CYA")
    // is never rewritten (and never pings anyone). Case-insensitive: agents
    // type "@alex" for a member named "Alex".
    const pattern = new RegExp(
      `(^|[\\s(\\[{])@${escapeRegExp(alias)}(?![\\p{L}\\p{N}_])`,
      'giu'
    );
    output = output.replace(pattern, `$1@${digits}`);
  });

  return output;
};

/**
 * Jids of every roster member whose `@<digits>` token appears in the text —
 * the mention metadata WhatsApp needs alongside the tokens.
 * @param {string} text - outgoing text AFTER convertGroupMentionsToTokens
 * @param {Array} participants - group roster rows ({ id, digits, ... })
 * @returns {string[]}
 */
export const collectGroupMentionJids = (text, participants = []) =>
  (participants || [])
    .filter(
      p =>
        p &&
        p.id &&
        p.digits &&
        new RegExp(`@${escapeRegExp(String(p.digits))}(?!\\d)`).test(text || '')
    )
    .map(p => p.id);
